/* eslint-disable react/prop-types */
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { cloneDeep, isEqual } from "lodash";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { logger } from "../../utils/logger";
import {
  AppContextType,
  ListViewContext,
  ListViewsContextType,
  OptionType,
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import { funcExecutor } from "../../functions/generic-functions";
import { evaluateExpression } from "../../functions/parser";
import {
  ExtendedEvents,
  FunctionDefinition,
  NameValueTuple,
} from "../model/form-configuration";
import HelperText from "./HelperText";
import { ControlledAutoCompleteProps } from "./PPField";

type InnerControlledAutoCompleteProps = {
  props: ControlledAutoCompleteProps;
  field: ControllerRenderProps<FieldValues, string>;
};

const InnerAutoComplete: FC<InnerControlledAutoCompleteProps> = ({
  props,
  field,
}: InnerControlledAutoCompleteProps) => {
  const fontColor: string = "text.secondary";
  const fieldIsRequired: string = "Field is required";
  const { control, getValues, setValue } = useFormContext();
  const stateCtx: StateContextType = useContext(StateContext);
  const lstContext = useContext(ListViewContext);

  const appContext = stateCtx.applicationContext;
  const setAppContext = stateCtx.setApplicationContext;

  const listsContext = lstContext.listViewsState;
  const setListContext = lstContext.setListViewsState;

  const requiredTokens: string[] = props.extendedAttributes["required"]
    ? props.extendedAttributes["required"].split(" ")
    : [];
  const watch: FieldValues = useWatch({ control, defaultValue: undefined });
  const isRequired: boolean = evaluateExpression(requiredTokens, watch);

  // Add loading state for this field
  const [isLoading, setIsLoading] = useState(false);

  // Get current options from context, falling back to props.options
  const currentOptions =
    appContext.autoCompleteOptions?.[props.id]?.currentOptions || props.options;
    
  // Initialize original options when the component mounts
  useEffect(() => {
    // Only initialize if we have options from props and they don't exist in context
    if (props.options && (!appContext.autoCompleteOptions?.[props.id] || !appContext.autoCompleteOptions?.[props.id]?.originalOptions)) {
      logger.debug(`Initializing original options for ${props.id}`);
      
      // If this is field 13118, add extra logging
      if (props.id === "13118") {
        logger.important(`Initializing original options for locality field (13118) with ${props.options.length} options`);
        logger.debug(`Sample original options:`, props.options.slice(0, 3));
      }
      
      // Save original options to context
      setAppContext((prev) => {
        const newContext = cloneDeep(prev);
        
        // Initialize autoCompleteOptions structure if needed
        if (!newContext.autoCompleteOptions) {
          newContext.autoCompleteOptions = {};
        }
        
        // Initialize this field's options
        newContext.autoCompleteOptions[props.id] = {
          originalOptions: props.options,
          currentOptions: props.options
        };
        
        return newContext;
      });
    }
  }, [props.id, props.options, appContext.autoCompleteOptions, setAppContext]);
    
  // Add event listener for loading state changes
  useEffect(() => {
    logger.debug(`Setting up loadingStateChanged event listener for field ${props.id}`);
    
    const handleLoadingStateChanged = (event: CustomEvent) => {
      logger.debug(`loadingStateChanged event received:`, {
        targetField: event.detail?.targetField,
        propsId: props.id,
        eventDetail: event.detail
      });
      
      if (event.detail && event.detail.targetField === props.id) {
        logger.debug(`Event matches our field ${props.id}, setting isLoading to:`, event.detail.isLoading);
        setIsLoading(event.detail.isLoading);
        
        // Special logging for field 13118
        if (props.id === "13118") {
          logger.important(`Locality field (13118) loading state changed to: ${event.detail.isLoading}`);
        }
      } else {
        logger.debug(`Event does not match our field ${props.id} (target: ${event.detail?.targetField}), ignoring`);
      }
    };

    // Add event listener to the document
    document.addEventListener('loadingStateChanged', handleLoadingStateChanged as EventListener);

    // Clean up event listener when component unmounts
    return () => {
      logger.debug(`Cleaning up loadingStateChanged event listener for field ${props.id}`);
      document.removeEventListener('loadingStateChanged', handleLoadingStateChanged as EventListener);
    };
  }, [props.id, setIsLoading]);
    
  // Add global cursor change during loading
  useEffect(() => {
    if (isLoading) {
      // Set global wait cursor
      document.body.style.cursor = 'wait';
      logger.debug(`Set global cursor to wait for field ${props.id}`);
    } else {
      // Remove global wait cursor
      document.body.style.cursor = '';
      logger.debug(`Removed global wait cursor for field ${props.id}`);
    }

    // Cleanup function to ensure cursor is reset if component unmounts while loading
    return () => {
      if (isLoading) {
        document.body.style.cursor = '';
        logger.debug(`Cleanup: Removed global wait cursor for field ${props.id}`);
      }
    };
  }, [isLoading, props.id]);
    
  // Add event listener for options changes
  useEffect(() => {
    // This function will handle the optionsChanged event
    const handleOptionsChanged = (event: CustomEvent) => {
      if (event.detail && event.detail.targetField === props.id) {
        logger.debug(`Received optionsChanged event for ${props.id}:`, event.detail);
        logger.debug(`Options count in event: ${event.detail.options?.length ?? 0}`);
        
        // Check if this is for field 13118 to more specifically debug our issue
        if (props.id === "13118") {
          logger.important(`Field 13118 (locality) received ${event.detail.options?.length ?? 0} options`);
          
          // Log a sample of the options for verification
          if (event.detail.options && event.detail.options.length > 0) {
            logger.debug(`Sample options:`, event.detail.options.slice(0, 3));
          } else {
            logger.warn(`No options received for locality field 13118`);
          }
        }
        
        // Update options in application context
        if (setAppContext && event.detail.options) {
          setAppContext((prev) => {
            const newContext = cloneDeep(prev);
            
            // Ensure autoCompleteOptions is initialized for this field
            if (!newContext.autoCompleteOptions[props.id]) {
              logger.debug(`Initializing autoCompleteOptions for ${props.id}`);
              newContext.autoCompleteOptions[props.id] = {
                originalOptions: props.options || [],
                currentOptions: props.options || []
              };
            }
            
            // Update current options
            const prevOptionsCount = newContext.autoCompleteOptions[props.id]?.currentOptions?.length || 0;
            newContext.autoCompleteOptions[props.id].currentOptions = event.detail.options;
            logger.debug(`Updated options for ${props.id} from ${prevOptionsCount} to ${event.detail.options.length}`);
            
            // Reset value if requested and current value is invalid
            if (event.detail.resetValue && field.value) {
              logger.debug(`Will reset value for ${props.id}`);
              setTimeout(() => {
                setValue(props.id, null);
              }, 0);
            }
            
            return newContext;
          });
        }
      }
    };

    // Add event listener to the document
    document.addEventListener('optionsChanged', handleOptionsChanged as EventListener);

    // Clean up event listener when component unmounts
    return () => {
      document.removeEventListener('optionsChanged', handleOptionsChanged as EventListener);
    };
  }, [props.id, field, setValue, setAppContext, props.options]);

  const getParameter = (nameValue: NameValueTuple): any => {
    let param: any = undefined;
    switch (nameValue.type) {
      case "string":
        param = getString(nameValue);
        break;
      case "stringArray":
        param = getStringArray(nameValue);
        break;
      case "rpn":
        param = getExpression(nameValue);
        break;
      default:
        param = nameValue.value;
    }
    return param;
  };

  const getString = (nameValue: NameValueTuple): string => nameValue.value;

  const getExpression = (nameValue: NameValueTuple): string => nameValue.value;

  const getStringArray = (nameValue: NameValueTuple): string[] => {
    const stringArrRegEx: RegExp = /\[(.*?)\]/;
    const stringVals: RegExpMatchArray | null =
      nameValue.value.match(stringArrRegEx);
    if (stringVals !== null) {
      return stringVals[1].split(",");
    } else return [] as string[];
  };

  const onChange = (
    appContext: AppContextType,
    listsContext: ListViewsContextType
  ) => {
    const events: ExtendedEvents | undefined = props.extendedEvents;

    if (events?.onChange) {
      const functions: FunctionDefinition[] = events.onChange;
      const sortedFunctions: FunctionDefinition[] = functions
        .slice()
        .sort(
          (a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order
        );

      sortedFunctions.forEach((func: FunctionDefinition) => {
        const sortedParams: NameValueTuple[] = func.parameters
          .slice()
          .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);

        funcExecutor(
          func.name,
          sortedParams.map((nameValue: NameValueTuple): any =>
            getParameter(nameValue)
          ),
          getValues,
          setValue,
          appContext,
          undefined,
          listsContext
        );
      });
    }
  };

  // Memoised so the Autocomplete keeps a stable array reference across renders.
  // MUI compares the value prop by reference and resets the typed input text
  // when it changes, so a fresh array on every render would wipe the search box
  // whenever an unrelated re-render happens (any form change via useWatch, any
  // appContext update).
  const multiValue = useMemo(
    () =>
      Array.isArray(field.value)
        ? field.value.filter((item: OptionType) => item?.id && item.id !== "")
        : [],
    [field.value]
  );

  // Helper function to get the appropriate value for the Autocomplete
  const getAutocompleteValue = () => {
    if (props.multiple) {
      // For multiple selection always return an array, never undefined. Returning
      // undefined flips the Autocomplete from controlled to uncontrolled, which
      // throws inside MUI when the field is remounted with a value by the stepper
      // and the last selection is then removed.
      return multiValue;
    } else {
      // For single selection, return the value or null
      if (field.value?.id && field.value.id !== "") {
        return field.value;
      }
      return null;
    }
  };

  const isFieldInvalid: boolean =
    appContext.fieldState[field.name] !== undefined &&
    !appContext.fieldState[field.name];
  const isHideField: boolean =
    appContext.fieldHide[field.name] !== undefined &&
    appContext.fieldHide[field.name];
  const isDisabledField: boolean =
    appContext.fieldDisable[field.name] !== undefined &&
    appContext.fieldDisable[field.name];

  return (
    <FormControl error={isFieldInvalid} fullWidth>
      <Autocomplete
        disabled={isDisabledField || isLoading}
        value={getAutocompleteValue()}
        isOptionEqualToValue={(option, value) => {
          // Handle null/undefined values
          if (!value || !option) return false;

          // Handle empty id values - consider them as null/undefined
          if (!value.id || value.id === "") return false;

          return option.id === value.id;
        }}
        id={props.id}
        multiple={props.multiple}
        options={currentOptions}
        getOptionLabel={(option) => option.value}
        filterSelectedOptions
        renderInput={(params) => {
          // Determine the sx styling based on field state
          let sxStyle = null;
          if (isHideField) {
            sxStyle = { display: "none" };
          } else if (isLoading) {
            sxStyle = { 
              cursor: "wait",
              "& .MuiInputBase-input": {
                cursor: "wait"
              },
              "& .MuiAutocomplete-input": {
                cursor: "wait"
              }
            };
          }

          // Debug logging for rendering state
          logger.debug(`ControlledAutoComplete ${props.id} rendering with:`, {
            isLoading,
            isHideField,
            isDisabledField,
            sxStyle,
            hasSpinner: isLoading,
            willShowCursor: isLoading,
            fieldWillBeDisabled: isDisabledField || isLoading,
            autocompleteDisabled: isDisabledField || isLoading
          });

          return (
            <TextField
              {...params}
              sx={sxStyle}
              required={false}
              id={props.id}
              disabled={isDisabledField || isLoading}
              label={
                <Typography
                  sx={{ color: isFieldInvalid ? red.A700 : fontColor }}
                >
                  {props.label + (isRequired ? " *" : "")}
                </Typography>
              }
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
        onChange={(_, data) => {
          field.onChange(data);
          const newAppContext: AppContextType = cloneDeep(appContext);
          const newListsContext: ListViewsContextType = cloneDeep(
            lstContext.listViewsState
          );

          // Validation logic for both single and multiple values
          let isValid = true;
          if (isRequired) {
            if (props.multiple) {
              // For multiple: check if array exists and has valid items
              isValid = Array.isArray(data) && data.length > 0;
            } else {
              // For single: check if value exists and has valid id
              isValid = data != null && data.id && data.id !== "";
            }
          }

          newAppContext.fieldState[field.name] = isValid;
          onChange(newAppContext, newListsContext);
          if (!isEqual(appContext, newAppContext)) {
            setAppContext(newAppContext);
          }
          if (!isEqual(listsContext, newListsContext)) {
            setListContext(newListsContext);
          }
        }}
      />
      <FormHelperText sx={isHideField ? { display: "none" } : null}>
        {isFieldInvalid ? (
          fieldIsRequired
        ) : (
          <HelperText helperText={props.helperText}></HelperText>
        )}
      </FormHelperText>
    </FormControl>
  );
};

const ControlledAutoComplete: FC<ControlledAutoCompleteProps> = (props) => {
  const { control } = useFormContext();
  return (
    <Controller
      key={props.id}
      render={({ field }) => {
        return <InnerAutoComplete props={props} field={field} />;
      }}
      defaultValue={props.defaultValue}
      name={props.id}
      control={control}
    />
  );
};

export default ControlledAutoComplete;
//ControlledAutoComplete.whyDidYouRender = true;
